import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, IconButton, TableRow, TextField, Tooltip, Zoom } from '@mui/material';
import { TableCellBody } from '../../table/tableCell';
import PersonRemoveAlt1Icon from '@mui/icons-material/PersonRemoveAlt1';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import QualityRejectedImg from '../../../assets/img/quality-rejected.jpg';
import QualityPassedImg from '../../../assets/img/quality-passed.jpg';
import DecisionPendingImg from '../../../assets/img/decision-pending.png';
import TestFailedImg from '../../../assets/img/testFailed.png';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store/appStore';
import { addId, clearIds, removeId } from '../../../redux/reducers/taskIdSlice';
import { useParams } from 'react-router-dom';
import { getProjectExperts, getRoles, updateProjectExpertTaskLimit } from '../../../redux/slices/projectSlice/addProjectSlice';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

interface TableRowItemProps {
  row: any;
  onDeleteClick: (userId: string, userType: string, stage_id: any) => void;
  onToggleDisable: (row: any) => void;
  onBumpedUpClick: any;
  alignment: string;
  editable: boolean;
  selectedStage: string;
}

const TableRowItem: React.FC<TableRowItemProps> = ({ row, onDeleteClick, onToggleDisable, onBumpedUpClick, alignment, editable, selectedStage }) => {
  const selectedIds = useSelector((state: RootState) => state.tasks.selectedIds);
  const dispatch = useDispatch<AppDispatch>();
  const lastSelectedIdRef = useRef<string | undefined>(undefined);
  const [localTaskLimit, setLocalTaskLimit] = useState(row.task_limit || '');
  const [isEditing, setIsEditing] = useState(false);
  const { id } = useParams();
  const prevStageRef = useRef<string | null>(null);
  const association_type = useSelector((state: RootState) => state.projects.association_type);
  const association_type_vendor = association_type && association_type === 'VENDOR';
  const projectData = useSelector((state: RootState) => state.projects);
  const isTestEnable = projectData?.project?.is_test_enabled

  const selectRange = (lastId: string, currentId: string) => {

    const lastIndex = selectedIds.findIndex((item) => item.id === lastId);
    const currentIndex = selectedIds.findIndex((item) => item.id === currentId);

    if (lastIndex === -1 || currentIndex === -1) {
      return;
    }

    const idsToSelect: string[] = [];
    const rangeStart = Math.min(lastIndex, currentIndex);
    const rangeEnd = Math.max(lastIndex, currentIndex);

    for (let i = rangeStart; i <= rangeEnd; i++) {
      idsToSelect.push(selectedIds[i].id);
    }

    idsToSelect.forEach((id) => {
      if (!selectedIds.some((selected) => selected.id === id)) {
        dispatch(addId({
          id,
          task_name: row.task_name
        }));
      }
    });
  };

  const handleCheckboxClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement, MouseEvent>, id: string) => {
      const checked = (event.target as HTMLInputElement).checked;
      const shiftKey = event.shiftKey;

      lastSelectedIdRef.current = lastSelectedIdRef.current || id;

      if (checked) {
        dispatch(addId({ id, task_name: row.task_name }));
      } else {
        dispatch(removeId(id));
      }

      if (shiftKey && lastSelectedIdRef.current) {
        selectRange(lastSelectedIdRef.current, id);
      }

      lastSelectedIdRef.current = id;
    },
    [dispatch, row.task_name]
  );

  useEffect(() => {
    if (prevStageRef.current !== null && prevStageRef.current !== selectedStage) {
      dispatch(clearIds());
    }

    prevStageRef.current = selectedStage;
  }, [selectedStage]);

  useEffect(() => {
    dispatch(getRoles({ project_id: id || '', stage_id: selectedStage }))
  }, [dispatch, id, selectedStage]);

  const handleSave = (row: any) => {
    const payload = {
      project_id: id,
      experts: [row.expert_id],
      fields_to_update: {
        task_limit: localTaskLimit ? parseInt(localTaskLimit) : undefined
      },
      stage_id: selectedStage
    }
    dispatch(updateProjectExpertTaskLimit(payload))
    dispatch(getProjectExperts({
      project_id: id,
      type: alignment === 'tasker' ? 'TASK' : 'REVIEW',
      paginate: true,
      stage_id: selectedStage,
      fields: JSON.stringify(['id', 'expert.id', 'expert.first_name', 'expert.user_name', 'task_limit', 'role_id', 'role.name', 'expert.last_name', 'expert.email', 'expert.pseudo_name', 'created_at', 'type', 'to_be_type', 'review_stage.name','is_qualified']),
    }))
    setIsEditing(true);
  }

  return (
    <TableRow
      key={row.id}
      sx={{
        '&:last-child td, &:last-child th': { borderBottom: 0 },
        backgroundColor: 'inherit',
      }}
    >
      <TableCellBody>
        <Checkbox
          checked={selectedIds.some(selected => selected.id === row.expert_id)}
          onClick={(event) => handleCheckboxClick(event as React.MouseEvent<HTMLInputElement, MouseEvent>, row.expert_id)}
          size='small'
        />
      </TableCellBody>
      <TableCellBody>{row.expert_first_name && row.expert_last_name ? (row.expert_first_name + " " + row.expert_last_name) : row.expert_last_name ? (row.expert_last_name) : '-'}</TableCellBody>
      <TableCellBody>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{row.expert_email ? row.expert_email : '-'}</span>

          {row.is_test_passed && alignment === 'tasker' && (
            <Tooltip title="Test Failed" arrow>
              <VerifiedIcon sx={{ color: 'green' }} />
            </Tooltip>
          )}
          {isTestEnable && !row.is_test_passed && alignment === 'tasker' && (
            <Tooltip title="Test Failed" placement="top">
              <span
                style={{
                  padding: '4px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '45px',
                }}
              >
                <img
                  src={TestFailedImg}
                  alt="Test Failed"
                  style={{
                    height: '28px',
                    width: '28px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              </span>
            </Tooltip>
          )}
          {row.is_blocked === true && (
            <Tooltip title="QC Rejected" placement="top">
              <span
                style={{
                  padding: '4px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '45px',
                }}
              >
                <img
                  src={QualityRejectedImg}
                  alt="Quality Rejected"
                  style={{
                    height: '28px',
                    width: '28px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              </span>
            </Tooltip>
          )}
          {row.is_qualified === true && (
            <Tooltip title={`QC Passed at ${row?.tasker_qualified_review_stages_list?.toString()}`} placement="top">
              <span
                style={{
                  padding: '4px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '45px',
                }}
              >
                <img
                  src={QualityPassedImg}
                  alt="Quality Passed"
                  style={{
                    height: '28px',
                    width: '28px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              </span>
            </Tooltip>
          )}
          {row.is_qualified === false && row.is_blocked === false && alignment === 'tasker' && (
            <Tooltip title="QC Decision Pending" placement="top">
              <span
                style={{
                  padding: '4px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '45px',
                }}
              >
                <img
                  src={DecisionPendingImg}
                  alt="QC Decision Pending"
                  style={{
                    height: '28px',
                    width: '28px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              </span>
            </Tooltip>
          )}
        </div>
      </TableCellBody>
      <TableCellBody>{row.gigsource_id ? row.gigsource_id : '-'}</TableCellBody>
      <TableCellBody>{row.role_name ? row.role_name : row.role_name}</TableCellBody>
      <TableCellBody>
        {/* {(editable || selectedIds.some(selected => selected.id === row.expert_id)) ? ( */}
        {isEditing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              type="number"
              size="small"
              value={localTaskLimit}
              variant="outlined"
              sx={{ width: 80 }}
              onChange={(e) => setLocalTaskLimit(e.target.value)}
            />
            <IconButton
              size="small"
              color="success"
              onClick={() => handleSave(row)}
            >
              <CheckIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setLocalTaskLimit(row.task_limit || '');
                setIsEditing(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => setIsEditing(true)}
          >
            {row.task_limit || '-'}
            <IconButton size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {/* ) : (
          row.task_limit || '-'
        )} */}
      </TableCellBody>

      <TableCellBody>{row.association_type ? row.association_type : '-'}</TableCellBody>

      <TableCellBody>
        <Tooltip title="Click to Deboard Expert" arrow slots={{ transition: Zoom }}>
          <IconButton sx={{ color: '#C00F00', cursor: 'pointer' }} onClick={() => onDeleteClick(row.expert_id, row.to_be_type, row.stage_id)}>
            <PersonRemoveAlt1Icon />
          </IconButton>
        </Tooltip>

        {(alignment === 'tasker' && !association_type_vendor && isTestEnable !== false) &&
          <Tooltip title="Promote the expert and allow direct task allocation." arrow slots={{ transition: Zoom }}>
            <IconButton sx={{ color: 'green', cursor: 'pointer' }} onClick={() => onBumpedUpClick(row.expert_id)}>
              <CheckCircleOutlineOutlinedIcon />
            </IconButton>
          </Tooltip>
        }

        <Tooltip title={`This User is ${row.is_blocked ? 'Blocked' : 'Unblocked'}. Click on the ${row.is_blocked ? 'Unblock' : 'Block'} icon in the action to ${row.is_blocked ? 'Unblock' : 'Block'}.`} arrow slots={{ transition: Zoom }}>
          <DoDisturbIcon
            sx={{
              color: row.is_blocked ? 'grey' : 'red',
              cursor: 'pointer',
              marginLeft: '5px',
            }}
            onClick={() => onToggleDisable(row)}
          />
        </Tooltip>
      </TableCellBody>
    </TableRow>
  );
};

export default TableRowItem;
